import { useCallback, useEffect, useState } from 'react';
import { fetchMemberOpsHistory, formatIstDateTime, opsEventTone } from '../../utils/marketingApi';
import './MemberActivityLog.css';

const PAGE_SIZE = 5;

export default function MemberActivityLog({ phone, batchId, refreshKey = 0, scrollable = false }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(async (requestLimit, { append = false } = {}) => {
    if (!phone) {
      setEvents([]);
      setTotal(0);
      return;
    }
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const data = await fetchMemberOpsHistory(phone, {
        batchId,
        limit: requestLimit,
        offset: 0,
      });
      setEvents(data?.events || []);
      setTotal(data?.total || 0);
      setLimit(requestLimit);
    } catch {
      if (!append) {
        setEvents([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [phone, batchId]);

  useEffect(() => {
    setLimit(PAGE_SIZE);
    fetchPage(PAGE_SIZE);
  }, [phone, batchId, refreshKey, fetchPage]);

  const loadMore = () => {
    const nextLimit = Math.min(limit + PAGE_SIZE, total || limit + PAGE_SIZE);
    fetchPage(nextLimit, { append: true });
  };

  const collapse = () => {
    fetchPage(PAGE_SIZE);
  };

  if (!phone) {
    return (
      <div className="mal-section">
        <div className="mal-head">
          <span className="mal-title">Activity log</span>
        </div>
        <div className="mal-empty">Phone not available for this contact.</div>
      </div>
    );
  }

  const remaining = Math.max(0, total - events.length);
  const canLoadMore = remaining > 0 && events.length < total;
  const canCollapse = limit > PAGE_SIZE && events.length > PAGE_SIZE;

  return (
    <div className={`mal-section${scrollable ? ' mal-section--footer' : ''}`}>
      <div className="mal-head">
        <span className="mal-title">Activity log</span>
        {total > 0 ? <span className="mal-count">{total} logged</span> : null}
      </div>
      {loading && events.length === 0 ? (
        <div className="mal-empty">Loading activity…</div>
      ) : events.length === 0 ? (
        <div className="mal-empty">No previous activity for this contact.</div>
      ) : (
        <div className={scrollable ? 'mal-scroll-wrap' : undefined}>
          <ul className="mal-list">
            {events.map((ev) => {
              const tone = opsEventTone(ev.action_type, ev.outcome);
              return (
                <li key={ev.id} className="mal-item">
                  <span className={`mal-dot mal-dot--${tone}`} aria-hidden />
                  <div className="mal-body">
                    <div className="mal-line">
                      <strong>{ev.action_label}</strong>
                      {ev.actor_label ? (
                        <span className="mal-actor"> · {ev.actor_label}</span>
                      ) : null}
                    </div>
                    <div className="mal-meta">
                      <time dateTime={ev.created_at} title={ev.created_at || undefined}>
                        {formatIstDateTime(ev.created_at)}
                      </time>
                      {ev.from_other_batch && ev.batch_id ? (
                        <span className="mal-batch-tag">from batch #{ev.batch_id}</span>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {!loading && (canLoadMore || canCollapse) ? (
        <div className="mal-expand-row">
          {canLoadMore ? (
            <button
              type="button"
              className="mal-expand"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, remaining)} more`}
            </button>
          ) : null}
          {canCollapse ? (
            <button type="button" className="mal-expand mal-expand--muted" onClick={collapse}>
              Show latest {PAGE_SIZE}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
